import $ from 'jquery';
import { createAddressSearchGuard } from './address_search_guard';

const selector = 'textarea[name$="[receiver_address_line]"], input[name$="[receiver_address_line]"], input[name$="[address]"], textarea[name$="[address]"]';
const instances = new Map();
let nextId = 0;
const requestGuard = createAddressSearchGuard();
function reserveRequest() {
  if (!requestGuard.consume().allowed) {
    const error = new Error('Address search limit reached');
    error.code = 'ADDRESS_SEARCH_LIMIT';
    throw error;
  }
}
// One shared SDK load across address fields and Turbolinks visits.
function googlePlaces(config) {
  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    return window.google.maps.importLibrary('places');
  }
  if (!window.__carrylugPlacesReady) {
    window.__carrylugPlacesReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = setTimeout(() => fail(), 15000);
      function fail() {
        clearTimeout(timeout);
        script.remove();
        delete window.__carrylugPlacesReady;
        delete window.__carrylugPlacesLoaded;
        reject(new Error('Google Places could not load'));
      }
      window.__carrylugPlacesLoaded = () => {
        clearTimeout(timeout);
        delete window.__carrylugPlacesLoaded;
        resolve(window.google.maps.importLibrary('places'));
      };
      script.src = 'https://maps.googleapis.com/maps/api/js?' + new URLSearchParams({
        key: config.apiKey, v: 'weekly', loading: 'async', libraries: 'places',
        callback: '__carrylugPlacesLoaded', language: config.lang
      });
      script.async = true;
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return window.__carrylugPlacesReady;
}

function withTimeout(promise) {
  let timer;
  return Promise.race([promise, new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error('Address search timed out')), 10000);
  })]).finally(() => clearTimeout(timer));
}

export function addressFields(place) {
  const components = place.addressComponents || [];
  const component = (type, short = false) => {
    const value = components.find(item => item.types.includes(type));
    return value ? (short ? value.shortText : value.longText) || '' : '';
  };
  const suffix = component('postal_code_suffix');
  const postcode = component('postal_code');
  return {
    receiver_address_line: place.formattedAddress || '',
    receiver_city: component('locality') || component('postal_town') || component('sublocality_level_1') || component('administrative_area_level_2'),
    receiver_state: component('administrative_area_level_1'),
    receiver_postal_code: postcode && suffix ? `${postcode}-${suffix}` : postcode,
    country_code: component('country', true).toLowerCase(),
    country: component('country')
  };
}

function initialize(input, config) {
  const receiver = input.name.endsWith('[receiver_address_line]');
  const section = receiver ? input.closest('.sending_section') : input.closest('form');
  if (!section) return;
  const prefix = input.name.slice(0, input.name.lastIndexOf('['));
  const fields = name => Array.from(section.querySelectorAll('input[name], select[name], textarea[name]'))
    .filter(field => field.name === `${prefix}[${name}]`);
  const country = fields(receiver ? 'receiver_country' : 'country').find(field => field.tagName === 'SELECT');
  if (!country || instances.has(input)) return;
  const host = input.closest('.form-group') || input.parentElement;
  host.classList.add('address-autocomplete-host');
  const list = document.createElement('div');
  list.className = 'address-autocomplete-results';
  list.id = `address-suggestions-${++nextId}`;
  list.setAttribute('role', 'listbox');
  list.hidden = true;
  const status = document.createElement('small');
  status.className = 'address-autocomplete-status';
  status.setAttribute('role', 'status');
  const anchor = input.closest('.compact-field-frame') || input;
  anchor.insertAdjacentElement('afterend', list);
  list.insertAdjacentElement('afterend', status);
  const attributes = ['role', 'aria-autocomplete', 'aria-controls', 'aria-expanded', 'autocomplete'];
  const previous = attributes.map(name => input.getAttribute(name));
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', list.id);
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('autocomplete', 'off');
  let timer, generation = 0, results = [], active = -1, composing = false, sessionToken, sessionStarted = 0;

  let pendingSearch = null, recentSearch = null;
  function suggestions(places, request) {
    const matches = entry => entry && entry.input === request.input && entry.token === request.sessionToken;
    if (matches(recentSearch) && Date.now() - recentSearch.time < 30000) return Promise.resolve(recentSearch.body);
    if (matches(pendingSearch)) return pendingSearch.promise;
    reserveRequest();
    const promise = withTimeout(Promise.resolve().then(() => places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)))
      .then(body => {
        recentSearch = { input: request.input, token: request.sessionToken, body, time: Date.now() };
        return body;
      }).finally(() => { if (pendingSearch && pendingSearch.promise === promise) pendingSearch = null; });
    pendingSearch = { input: request.input, token: request.sessionToken, promise };
    return promise;
  }

  function positionList() {
    const fieldRect = input.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    list.style.left = `${fieldRect.left - hostRect.left - host.clientLeft + host.scrollLeft}px`;
    list.style.top = `${fieldRect.bottom - hostRect.top - host.clientTop + host.scrollTop}px`;
    list.style.width = `${fieldRect.width}px`;
    list.style.right = 'auto';
  }
  window.addEventListener('resize', positionList);

  function close() {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  }

  function cancel() {
    clearTimeout(timer);
    generation += 1;
    close();
  }

  function countryOption(address) {
    const code = String(address.country_code || '').toLowerCase();
    const matches = item => item.value && (
      (code && String(config.countries[item.value] || '').toLowerCase() === code) ||
      item.value.toLowerCase() === String(address.country || '').toLowerCase()
    );
    // A legacy database name and the standard name may share the same ISO code.
    const selected = country.options[country.selectedIndex];
    return selected && matches(selected) ? selected : Array.from(country.options).find(matches);
  }

  async function select(index) {
    const prediction = results[index];
    if (!prediction) return;
    results = []; // Prevent touch/click duplicates from fetching paid details twice.
    cancel();
    const version = generation;
    sessionToken = null;
    try {
      reserveRequest();
      const place = prediction.toPlace();
      await withTimeout(place.fetchFields({ fields: ['formattedAddress', 'addressComponents'] }));
      if (version !== generation || !input.isConnected) return;
      const address = addressFields(place);
      const option = countryOption(address);
      if (!option) {
        status.textContent = config.empty;
        return;
      }
      if (option) {
        country.value = option.value;
        $(country).trigger('change');
      }
      Object.entries(address).filter(([name]) => name.startsWith('receiver_')).forEach(([name, value]) => {
        const targetName = receiver ? name : name === 'receiver_address_line' ? 'address' : name.replace('receiver_', '');
        fields(targetName).forEach(field => {
          if (field.disabled || field.readOnly) return;
          if (field.tagName === 'SELECT' && value && !Array.from(field.options).some(item => item.value === value)) {
            field.add(new Option(value, value));
          }
          field.value = value;
          $(field).trigger('change');
        });
      });
      status.textContent = '';
    } catch (error) {
      if (version === generation) status.textContent = error.code === 'ADDRESS_SEARCH_LIMIT' ? config.limited : config.unavailable;
    }
  }

  function schedule() {
    cancel();
    status.textContent = '';
    if (composing || input.disabled || input.readOnly || input.value.trim().length < 3) return;
    const version = generation;
    timer = setTimeout(async () => {
      try {
        const places = await googlePlaces(config);
        if (version !== generation || !input.isConnected) return;
        if (!sessionToken || Date.now() - sessionStarted > 180000) {
          sessionToken = new places.AutocompleteSessionToken();
          sessionStarted = Date.now();
        }
        const request = { input: input.value.trim(), language: config.lang, sessionToken };
        const body = await suggestions(places, request);
        if (version !== generation || !input.isConnected) return;
        results = (body.suggestions || []).map(item => item.placePrediction).filter(Boolean).slice(0, 5);
        list.replaceChildren();
        results.forEach((address, index) => {
          const item = document.createElement('div');
          item.id = `${list.id}-${index}`;
          item.className = 'address-autocomplete-option';
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');
          item.textContent = address.text.toString();
          item.addEventListener('mousedown', event => event.preventDefault());
          item.addEventListener('click', () => select(index));
          item.addEventListener('touchend', event => { event.preventDefault(); select(index); }, { passive: false });
          list.appendChild(item);
        });
        if (results.length) {
          const attribution = document.createElement('div');
          attribution.className = 'address-autocomplete-google';
          attribution.setAttribute('translate', 'no');
          attribution.textContent = 'Google Maps';
          list.appendChild(attribution);
        }
        positionList();
        list.hidden = !results.length;
        input.setAttribute('aria-expanded', String(!!results.length));
        status.textContent = results.length ? '' : config.empty;
      } catch (error) {
        if (version === generation) status.textContent = error.code === 'ADDRESS_SEARCH_LIMIT' ? config.limited : config.unavailable;
      }
    }, 350);
  }

  function keydown(event) {
    if (event.key === 'Escape' || event.key === 'Tab') { cancel(); return; }
    if (list.hidden || composing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      active = active < 0 ? (event.key === 'ArrowDown' ? 0 : results.length - 1) :
        (active + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length;
      Array.from(list.querySelectorAll('[role="option"]')).forEach((item, index) => item.setAttribute('aria-selected', String(index === active)));
      input.setAttribute('aria-activedescendant', list.children[active].id);
      list.children[active].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      select(active);
    }
  }

  const startComposition = () => { composing = true; cancel(); };
  const endComposition = () => { composing = false; schedule(); };
  const changeCountry = () => { cancel(); sessionToken = null; status.textContent = ''; };
  input.addEventListener('input', schedule);
  input.addEventListener('keydown', keydown);
  input.addEventListener('blur', cancel);
  input.addEventListener('compositionstart', startComposition);
  input.addEventListener('compositionend', endComposition);
  $(country).on('change.googlePlaces', changeCountry);
  instances.set(input, () => {
    cancel();
    window.removeEventListener('resize', positionList);
    input.removeEventListener('input', schedule);
    input.removeEventListener('keydown', keydown);
    input.removeEventListener('blur', cancel);
    input.removeEventListener('compositionstart', startComposition);
    input.removeEventListener('compositionend', endComposition);
    $(country).off('change.googlePlaces', changeCountry);
    attributes.forEach((name, index) => previous[index] === null ? input.removeAttribute(name) : input.setAttribute(name, previous[index]));
    list.remove(); status.remove();
    host.classList.remove('address-autocomplete-host');
  });
}

function initializeAll() {
  instances.forEach((destroy, input) => {
    if (!input.isConnected) { destroy(); instances.delete(input); }
  });
  const meta = document.querySelector('meta[name="address-autocomplete-config"]');
  if (!meta) return;
  const config = JSON.parse(meta.content);
  if (!config.apiKey) return;
  document.querySelectorAll(selector).forEach(input => initialize(input, config));
}

// AJAX partials and dynamically inserted profile forms also need initialization.
const observer = new MutationObserver(records => {
  if (records.some(record => Array.from(record.addedNodes).some(node =>
    node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
  )) || Array.from(instances.keys()).some(input => !input.isConnected)) initializeAll();
});
function start() {
  initializeAll();
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
}
$(start);
$(document).on('turbolinks:load shown.bs.modal ajaxComplete cocoon:after-insert', start);
$(document).on('turbolinks:before-cache', () => {
  observer.disconnect();
  instances.forEach(destroy => destroy());
  instances.clear();
});
